/**
 * DevDash Gating Logic
 *
 * Controls when DevDash is available:
 *   1. OFF if environment is prod or stage
 *   2. OFF if env var NEXT_PUBLIC_DEVDASH_DISABLED=true
 *   3. ON if localStorage devdash.enabled=true (user opted in via URL)
 *   4. OFF otherwise (opt-in required)
 *
 * Users must visit /?devdash=true to enable DevDash.
 * This sets localStorage and persists access across sessions.
 */

const STORAGE_KEY_ENABLED = 'devdash.enabled';
const URL_PARAM_ENABLE = 'devdash';

/**
 * Get the current environment
 */
// biome-ignore lint/nursery/useExportsLast: Function exported at definition for code organization
export function getCurrentEnvironment(): string {
  // biome-ignore lint/style/noProcessEnv: DevDash needs direct environment access for gating
  return process.env.ENVIRONMENT?.toLowerCase() || 'local';
}

/**
 * Check if we're in a production-like environment (stage or prod)
 */
function isProductionEnvironment(): boolean {
  const env = getCurrentEnvironment();
  return env === 'prod' || env === 'stage' || env === 'production';
}

/**
 * Check if DevDash is disabled via environment variable
 */
function isDisabledByEnvVar(): boolean {
  // biome-ignore lint/style/noProcessEnv: DevDash needs direct environment access for gating
  return process.env.NEXT_PUBLIC_DEVDASH_DISABLED === 'true';
}

/**
 * Check if the user has enabled DevDash via localStorage (opt-in)
 */
export function isDevDashEnabledByUser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(STORAGE_KEY_ENABLED) === 'true';
  } catch {
    return false;
  }
}

/**
 * Enable DevDash by setting localStorage flag
 */
export function enableDevDash(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY_ENABLED, 'true');
  } catch {
    // Ignore storage errors
  }
}

/**
 * Disable DevDash by removing localStorage flag (logout)
 */
export function disableDevDash(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY_ENABLED);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Check URL for DevDash enable parameter and set localStorage if found
 * Call this on app initialization to handle the magic URL
 * Returns true ONLY if DevDash was just ACTIVATED (not deactivated)
 */
export function checkUrlForDevDashEnable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const devdashParam = urlParams.get(URL_PARAM_ENABLE);

    if (devdashParam === 'true') {
      enableDevDash();

      // Clean the URL by removing the devdash parameter
      urlParams.delete(URL_PARAM_ENABLE);
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);

      // Return true to indicate activation just happened
      return true;
    }

    // Also support ?devdash=false to logout
    if (devdashParam === 'false') {
      disableDevDash();

      // Clean the URL
      urlParams.delete(URL_PARAM_ENABLE);
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);

      // Return false - this was deactivation, not activation
      return false;
    }
  } catch {
    // Ignore errors
  }

  return false;
}

/**
 * Main gating function - determines if DevDash should be available
 *
 * Gating rules (checked in order):
 *   1. OFF if environment is prod or stage
 *   2. OFF if env var NEXT_PUBLIC_DEVDASH_DISABLED=true
 *   3. ON if localStorage devdash.enabled=true
 *   4. OFF otherwise (opt-in required)
 */
export function isDevDashEnabled(): boolean {
  // 1. Production environments - always disabled
  if (isProductionEnvironment()) {
    return false;
  }

  // 2. Environment variable override
  if (isDisabledByEnvVar()) {
    return false;
  }

  // 3. User must have opted in via localStorage
  if (isDevDashEnabledByUser()) {
    return true;
  }

  // 4. Disabled by default - opt-in required
  return false;
}

/**
 * Get environment badge color for display
 */
export function getEnvBadgeColor(): string {
  const env = getCurrentEnvironment();
  switch (env) {
    case 'local':
      return '#10b981'; // Green
    case 'dev':
      return '#3b82f6'; // Blue
    case 'qa':
      return '#8b5cf6'; // Purple
    default:
      return '#6b7280'; // Gray
  }
}
